import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAlert } from "react-alert";
import Loader from "../../Loader";

const _api_path = process.env.REACT_APP_API_BASE_URL || "/api/v1";

// Admin screen for currency / FX configuration consumed by the mobile app
// Paystack flow. Settings are persisted server-side via /admin/currency-settings.
const CurrencySettings = () => {
  const alert = useAlert();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [liveRate, setLiveRate] = useState(null);
  const [liveError, setLiveError] = useState(null);

  // Form state
  const [ghsEnabled, setGhsEnabled] = useState(true);
  const [fxBufferPct, setFxBufferPct] = useState("2"); // percentage in UI
  const [manualOverrideRate, setManualOverrideRate] = useState("");

  const hydrateForm = (data) => {
    setSettings(data);
    setGhsEnabled(!!data.ghsEnabled);
    setFxBufferPct(
      data.fxBuffer !== undefined && data.fxBuffer !== null
        ? String(Number((data.fxBuffer * 100).toFixed(4)))
        : "0",
    );
    setManualOverrideRate(
      data.manualOverrideRate !== null && data.manualOverrideRate !== undefined
        ? String(data.manualOverrideRate)
        : "",
    );
    setLiveRate(data.liveRate || null);
    setLiveError(data.liveError || null);
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${_api_path}/admin/currency-settings`);
      const payload = data?.data || data;
      hydrateForm(payload);
    } catch (err) {
      alert.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load currency settings",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();

    const bufferNumber = Number(fxBufferPct);
    if (!Number.isFinite(bufferNumber) || bufferNumber < 0 || bufferNumber > 100) {
      alert.error("FX buffer must be a percentage between 0 and 100.");
      return;
    }

    let overridePayload = null;
    if (manualOverrideRate !== "" && manualOverrideRate !== null) {
      const overrideNumber = Number(manualOverrideRate);
      if (!Number.isFinite(overrideNumber) || overrideNumber <= 0) {
        alert.error("Manual override rate must be a positive number, or empty.");
        return;
      }
      overridePayload = overrideNumber;
    }

    setSaving(true);
    try {
      const { data } = await axios.put(`${_api_path}/admin/currency-settings`, {
        ghsEnabled,
        fxBuffer: bufferNumber / 100,
        manualOverrideRate: overridePayload,
      });
      const payload = data?.data || data;
      hydrateForm({ ...settings, ...payload });
      alert.success("Currency settings saved.");
    } catch (err) {
      alert.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save currency settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const liveRateValue = liveRate ? Number(liveRate.rate) : null;
  const previewedEffectiveRate =
    liveRateValue !== null
      ? liveRateValue * (1 + Number(fxBufferPct || 0) / 100)
      : null;

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Currency <small>Mobile Paystack settings</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Currency Settings</li>
          </ol>
        </section>

        <section className="content">
          {loading ? (
            <Loader />
          ) : (
            <div className="row">
              <div className="col-md-8 col-xs-12">
                <div className="box box-primary">
                  <div className="box-header with-border">
                    <h3 className="box-title">NGN &harr; GHS configuration</h3>
                  </div>
                  <form onSubmit={handleSave}>
                    <div className="box-body">
                      <div className="form-group">
                        <label style={{ fontWeight: 400 }}>
                          <input
                            type="checkbox"
                            checked={ghsEnabled}
                            onChange={(e) => setGhsEnabled(e.target.checked)}
                            style={{ marginRight: 8 }}
                          />
                          Allow GHS (Cedi) as a payment currency in the mobile app
                        </label>
                        <p className="help-block">
                          When disabled, the GHS option is hidden / rejected on
                          checkout. NGN remains the default.
                        </p>
                      </div>

                      <div className="form-group">
                        <label htmlFor="fxBuffer">FX buffer (%)</label>
                        <input
                          id="fxBuffer"
                          type="number"
                          className="form-control"
                          min="0"
                          max="100"
                          step="0.1"
                          value={fxBufferPct}
                          onChange={(e) => setFxBufferPct(e.target.value)}
                        />
                        <p className="help-block">
                          Added on top of the live mid-market rate to absorb FX
                          swings between quote time and Paystack settlement
                          (recommended: 1&ndash;3%).
                        </p>
                      </div>

                      <div className="form-group">
                        <label htmlFor="manualRate">
                          Manual override rate (1 NGN = ? GHS)
                        </label>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input
                            id="manualRate"
                            type="number"
                            className="form-control"
                            min="0"
                            step="0.000001"
                            placeholder="Leave empty to use live rate"
                            value={manualOverrideRate}
                            onChange={(e) =>
                              setManualOverrideRate(e.target.value)
                            }
                          />
                          {manualOverrideRate !== "" && (
                            <button
                              type="button"
                              className="btn btn-default"
                              onClick={() => setManualOverrideRate("")}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <p className="help-block">
                          When set, this fixed rate is used and the FX buffer is
                          ignored. Useful if your FX provider is unreliable or
                          you&rsquo;ve negotiated a fixed rate with Paystack.
                        </p>
                      </div>
                    </div>
                    <div className="box-footer">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save settings"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-default"
                        style={{ marginLeft: 8 }}
                        onClick={loadSettings}
                        disabled={saving}
                      >
                        Refresh
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="col-md-4 col-xs-12">
                <div className="box box-info">
                  <div className="box-header with-border">
                    <h3 className="box-title">Live rate (NGN &rarr; GHS)</h3>
                  </div>
                  <div className="box-body">
                    {liveRateValue !== null ? (
                      <Fragment>
                        <p style={{ marginBottom: 4 }}>
                          <strong>Mid-market:</strong>{" "}
                          {liveRateValue.toFixed(6)} GHS per 1 NGN
                        </p>
                        <p style={{ marginBottom: 0 }}>
                          <small className="text-muted">
                            Source: {liveRate.source}
                          </small>
                        </p>
                        {settings?.manualOverrideRate ? (
                          <p
                            className="text-yellow"
                            style={{ marginTop: 12, marginBottom: 0 }}
                          >
                            Manual override is active &mdash; the live rate is
                            currently ignored.
                          </p>
                        ) : (
                          previewedEffectiveRate !== null && (
                            <p style={{ marginTop: 12, marginBottom: 0 }}>
                              <strong>Effective rate</strong> (with{" "}
                              {Number(fxBufferPct || 0).toFixed(2)}% buffer):{" "}
                              {previewedEffectiveRate.toFixed(6)}
                            </p>
                          )
                        )}
                      </Fragment>
                    ) : (
                      <p className="text-muted" style={{ marginBottom: 0 }}>
                        No live rate available.
                      </p>
                    )}
                    {liveError && (
                      <p
                        className="text-red"
                        style={{ marginTop: 12, marginBottom: 0 }}
                      >
                        {liveError}
                      </p>
                    )}
                  </div>
                </div>

                {settings?.updatedAt && (
                  <div className="box box-default">
                    <div className="box-header with-border">
                      <h3 className="box-title">Last updated</h3>
                    </div>
                    <div className="box-body">
                      <p style={{ marginBottom: 0 }}>
                        {new Date(settings.updatedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </Fragment>
  );
};

export default CurrencySettings;
